/**
 * Account settings form.
 */
import React from "react";
import { Box, Grid, MenuItem, Select, TextField, Divider } from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import * as Yup from 'yup';

import { useAsterController } from "context";
import { actionEditAccount } from "context/action";
import { VFormItem, VText, VButton } from "form";
import { COUNTRY_LIST } from "utils/string";

/**
 * Formik form that updates the learner profile.
 */
const SettingsAccount = () => {

  const { t } = useTranslation('common');

  const [controller, dispatch] = useAsterController();
  const { user, isProfileUpdateLoading, loadedDashboard } = controller;

  /**
   * Persists account field changes.
   *
   * @param {Object} values Form values.
   * @returns {void}
   */
  const onSubmit = (values) => actionEditAccount(dispatch, values, t);

  const formik = useFormik({
    initialValues: {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      gender: user.gender || 'm',
      year_of_birth: user.year_of_birth || '',
      bio: user.bio || '',
      phone_number: user.phone_number || '',
      company: user.company || '',
      func: user.func || '',
      role: user.role || '',
      country: user.country || 'MG',
      state: user.state || '',
      city: user.city || '',
      twitter: user.twitter || '',
      linkedin: user.linkedin || '',
      facebook: user.facebook || ''
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      first_name: Yup.string().max(20, 'Must be 15 characters or less').required('This field is required'),
      last_name: Yup.string().max(20, 'Must be 15 characters or less').required('This field is required'),
      phone_number: Yup.string().matches(/\+[0-9]+/, t('Phone number is not valid')),
      country: Yup.string().required('This field is required'),
    }),
    onSubmit: onSubmit
  });

  return <Box>

    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box className="flex sm:flex-row flex-col sm:items-center items-start justify-between">
            <Box className="flex w-full">
              <VText className="text-2xl font-bold">{t("General Information")}</VText>
            </Box>

            <Box className="flex justify-end w-full">
              <VButton variant="contained" color="primary" className="text-sm px-10 normal-case mt-3 md:mt-0" type="submit" loading={!isProfileUpdateLoading}>
                {t("Save")}
              </VButton>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Grid container spacing={2}>

            {
              loadedDashboard ? <>
                <Grid item xs={12} sm={6}>
                  <VFormItem formik={formik} label={t("First Name")} name="first_name" required>
                    <TextField name="first_name" placeholder="John" size="small" value={formik.values.first_name} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <VFormItem formik={formik} label={t("Last Name")} name="last_name" required>
                    <TextField name="last_name" size="small" placeholder="John" value={formik.values.last_name} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>

                <Grid item xs={12}>
                  <VFormItem formik={formik} label={t("Biography")} name="bio">
                    <TextField name="bio" size="small" placeholder="Enter a description..." value={formik.values.bio} onChange={formik.handleChange} rows={3} multiline className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <VFormItem formik={formik} label={t("Phone")} name="phone_number" required>
                    <TextField name="phone_number" size="small" placeholder="John" value={formik.values.phone_number} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                  <VText className="text-sm" color="secondary">{t("If you want to benefit from personalized follow-up by phone")}</VText>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <VFormItem formik={formik} label={t("Year of Birth")} name="year_of_birth">
                    <TextField name="year_of_birth" size="small" value={formik.values.year_of_birth} onChange={formik.handleChange} className="w-full" color="secondary" type="number" />
                  </VFormItem>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <VFormItem formik={formik} label={t("Gender")} name="gender" required>
                    <Select name="gender" size="small" value={formik.values.gender} onChange={formik.handleChange} className="w-full" color="secondary" >
                      <MenuItem value='m'>{t("Male")}</MenuItem>
                      <MenuItem value='f'>{t("Female")}</MenuItem>
                    </Select>
                  </VFormItem>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <VFormItem formik={formik} label={t("Country")} name="country" required>
                    <Select name="country" size="small" value={formik.values.country} onChange={e => { formik.setFieldValue('country', e.target.value); formik.setFieldValue('state', ''); }} className="w-full" color="secondary" >
                      {COUNTRY_LIST.map(v => <MenuItem key={v.name} value={v.code}>{v.name}</MenuItem>)}
                    </Select>
                  </VFormItem>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <VFormItem formik={formik} label={t("State")} name="state">
                    <Select name="state" size="small" value={formik.values.state} onChange={formik.handleChange} className="w-full" color="secondary" disabled={!COUNTRY_LIST.find(v => v.code === formik.values.country)}>
                      {(COUNTRY_LIST.find(v => v.code === formik.values.country) || { state: [] }).state.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                  </VFormItem>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <VFormItem formik={formik} label={t("City")} name="city">
                    <TextField name="city" size="small" placeholder="Argentina/testCity" value={formik.values.city} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>
              </> :
                <Grid item xs={12}>
                  <Skeleton count={5} className="mt-5 h-8" />
                </Grid>
            }


            <Grid item xs={12}>
              <VText className="text-2xl font-bold mt-4" div>{t("Your Business")}</VText>
            </Grid>

            {
              loadedDashboard ? <>
                <Grid item xs={12} sm={6}>
                  <VFormItem formik={formik} label={t("Company Name")} name="company">
                    <TextField name="company" placeholder="John" size="small" value={formik.values.company} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <VFormItem formik={formik} label={t("Function")} name="func">
                    <TextField name="func" size="small" placeholder="Director" value={formik.values.func} onChange={formik.handleChange} className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>

                <Grid item xs={12}>
                  <VFormItem formik={formik} label={t("Role")} name="role">
                    <TextField name="role" size="small" placeholder={t("Enter a description")} value={formik.values.role} onChange={formik.handleChange} rows={3} multiline className="w-full" color="secondary" />
                  </VFormItem>
                </Grid>
              </> :
                <Grid item xs={12}>
                  <Skeleton count={3} className="mt-5 h-8" />
                </Grid>
            }


            <Grid item xs={12}>
              <VText className="text-2xl font-bold mt-4" div>{t("Social Networks")}</VText>
            </Grid>

            {
              loadedDashboard ? <>
                <Grid item xs={12} sm={3}>
                  <VText className="text-md">{t("Twitter Profile")}</VText>
                </Grid>

                <Grid item xs={12} sm={9}>
                  <Box className="flex items-center border border-solid border-gray-300 dark:border-gray-500 rounded-md">
                    <VText className="px-3 py-2">twitter.com/</VText>
                    <Divider className="border-r border-solid border-gray-300 dark:border-gray-500" orientation="vertical" flexItem />
                    <TextField name="twitter" size="small" value={formik.values.twitter} onChange={formik.handleChange} className="w-full border-0" color="secondary" sx={{ '& fieldset': { borderRadius: '0 5px 5px 0' } }} />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <VText className="text-md">{t("Linkedin Profile")}</VText>
                </Grid>

                <Grid item xs={12} sm={9}>
                  <Box className="flex items-center border border-solid border-gray-300 dark:border-gray-500 rounded-md">
                    <VText className="px-3 py-2">linkedin.com/</VText>
                    <Divider className="border-r border-solid border-gray-300 dark:border-gray-500" orientation="vertical" flexItem />
                    <TextField name="linkedin" size="small" value={formik.values.linkedin} onChange={formik.handleChange} className="w-full border-0" color="secondary" sx={{ '& fieldset': { borderRadius: '0 5px 5px 0' } }} />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <VText className="text-md">{t("Facebook Profile")}</VText>
                </Grid>

                <Grid item xs={12} sm={9}>
                  <Box className="flex items-center border border-solid border-gray-300 dark:border-gray-500 rounded-md">
                    <VText className="px-3 py-2">facebook.com/</VText>
                    <Divider className="border-r border-solid border-gray-300 dark:border-gray-500" orientation="vertical" flexItem />
                    <TextField name="facebook" size="small" value={formik.values.facebook} onChange={formik.handleChange} className="w-full border-0" color="secondary" sx={{ '& fieldset': { borderRadius: '0 5px 5px 0' } }} />
                  </Box>
                </Grid>
              </> :
                <Grid item xs={12}>
                  <Skeleton count={3} className="mt-5 h-8" />
                </Grid>
            }

          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}></Grid>
      </Grid>
    </form>
  </Box >;
};

export default SettingsAccount;